import React, {
    forwardRef,
    useImperativeHandle,
    useRef,
    useMemo
} from 'react';
import {
    Box,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Grid,
    Typography,
    Card,
    CardHeader,
    CardContent,
    CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
    GridComponent,
    TooltipComponent,
    LegendComponent,
    DataZoomComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([
    BarChart,
    GridComponent, TooltipComponent, LegendComponent, DataZoomComponent,
    CanvasRenderer,
]);


const allNumeric = (arr) => arr.every((item) => !isNaN(parseFloat(item)));

const sortValues = (arr) => {
    if (!arr || arr.length === 0) return arr;
    if (allNumeric(arr)) {
        return arr
            .map((v) => parseFloat(v))
            .sort((a, b) => a - b)
            .map((v) => String(v));
    } else {
        return arr.sort((a, b) => String(a).localeCompare(String(b)));
    }
};

const transformAndSortChartData = (chartData) => {
    if (!chartData || chartData.length === 0) return [];
    const transformed = chartData.map((item) => ({
        label:
            item.x !== undefined && item.x !== null ? String(item.x) : 'Sin valor',
        value:
            typeof item.value === 'number'
                ? parseFloat(item.value.toFixed(2))
                : item.value
    }));
    const labels = transformed.map((obj) => obj.label);
    const numericLabels = allNumeric(labels);

    if (numericLabels) {
        return transformed.sort((a, b) => parseFloat(a.label) - parseFloat(b.label));
    } else {
        return transformed.sort((a, b) => a.label.localeCompare(b.label));
    }
};


const glassTooltip = {
    backgroundColor: 'rgba(255,255,255,0.88)',
    borderColor: '#E2E8F0',
    borderWidth: 1,
    padding: [8, 12],
    textStyle: { color: '#334155', fontSize: 13 },
    extraCssText: 'backdrop-filter:blur(6px);border-radius:8px;box-shadow:0 4px 24px rgba(0,0,0,0.08)',
};

const ChartTemplates = forwardRef(({
                                       availableProps,
                                       chartsConfig,
                                       analysisData,
                                       onChangeChartsConfig,
                                       onRemoveChart,
                                       onAnalyzeOneChart,
                                       distinctValuesMap,
                                       chartLoadingStates,
                                       harvestGeojson
                                   }, ref) => {

    const chartInstancesRef = useRef({});

    const randomColorsRef = useRef({});

    const getChartTitle = (chart, index) => {
        const safeIndex = Number.isFinite(index) ? index : 0;
        const opLabel = chart?.calcType === 'sum' ? 'Suma' : 'Promedio';
        const filterLabel = chart?.filterProp
            ? `Filtrado por ${chart.filterProp} = ${chart.filterValue || '(cualquiera)'}`
            : '';

        return chart?.xAxis && chart?.yAxis
            ? `${chart.xAxis} vs. ${chart.yAxis} (${opLabel}) ${filterLabel}`.trim()
            : `Gráfica #${safeIndex + 1}`;
    };

    const getRandomColor = (label) => {
        if (!randomColorsRef.current[label]) {
            const color = '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
            randomColorsRef.current[label] = color;
        }
        return randomColorsRef.current[label];
    };

    useImperativeHandle(ref, () => ({
        exportAllChartsAsImages: async () => {
            const instances = chartInstancesRef.current;
            const keys = Object.keys(instances);
            if (keys.length === 0) {
                console.error("No hay gráficas disponibles para exportar.");
                return [];
            }

            const images = [];
            for (const key of keys) {
                const inst = instances[key];
                if (inst) {
                    try {
                        const parsedIndex = Number(key);
                        const chartIndex = Number.isFinite(parsedIndex) ? parsedIndex : images.length;
                        const url = inst.getDataURL({
                            type: 'png',
                            pixelRatio: 3,
                            backgroundColor: '#fff',
                        });
                        images.push({
                            id: `template-${key}`,
                            title: getChartTitle(chartsConfig[chartIndex], chartIndex),
                            image: url,
                        });
                    } catch (error) {
                        console.error(`Error al capturar la gráfica ${key}:`, error);
                    }
                }
            }
            return images;
        }
    }));

    const handleConfigChange = (index, field, value) => {
        onChangeChartsConfig((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const toggleEditMode = (index, forceValue) => {
        onChangeChartsConfig((prev) => {
            const updated = [...prev];
            updated[index].editMode =
                typeof forceValue === 'boolean'
                    ? forceValue
                    : !updated[index].editMode;
            return updated;
        });
    };

    const handleDynamicFilterChange = (index, newFilterValue) => {
        onChangeChartsConfig((prev) => {
            const updated = [...prev];
            updated[index].filterValue = newFilterValue;
            return updated;
        });
        onAnalyzeOneChart(index);
    };

    const buildColorLogic = (geojson, xAxisProp, fallbackRandomColorFn) => {
        const normalized = xAxisProp
            ? xAxisProp.toLowerCase().replace(/\s+/g, '_')
            : '';

        const aggregatorKey = 'agrupacion_' + normalized;
        const colorKey = 'color_' + normalized;

        const aggregatorColorMap = {};
        if (geojson?.features && normalized) {
            geojson.features.forEach((feature) => {
                const props = feature.properties || {};
                const agValue = props[aggregatorKey];
                const colValue = props[colorKey];
                if (agValue && colValue) {
                    if (!aggregatorColorMap[agValue]) {
                        aggregatorColorMap[agValue] = colValue;
                    }
                }
            });
        }

        const getBarColor = (label) => {
            if (aggregatorColorMap[label]) {
                return aggregatorColorMap[label];
            }
            return fallbackRandomColorFn(label);
        };

        return { aggregatorColorMap, getBarColor };
    };

    const renderBarChart = (data, title, chartIndex, currentChartConfig) => {
        const { aggregatorColorMap, getBarColor } =
            buildColorLogic(harvestGeojson, currentChartConfig?.xAxis, getRandomColor);

        const option = {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                ...glassTooltip,
            },
            xAxis: {
                type: 'category',
                data: data.map(d => d.label),
                axisLabel: { rotate: -40, fontSize: 14, color: '#000', interval: 0 },
                axisLine: { lineStyle: { color: '#000' } },
                axisTick: { lineStyle: { color: '#000' } },
                name: 'Eje X',
                nameLocation: 'middle',
                nameGap: 60,
                nameTextStyle: { fontSize: 18, fontWeight: 'bold', color: '#000' },
            },
            yAxis: {
                type: 'value',
                axisLabel: { fontSize: 14, color: '#000' },
                axisLine: { lineStyle: { color: '#000' } },
                axisTick: { lineStyle: { color: '#000' } },
                name: 'Eje Y',
                nameLocation: 'middle',
                nameGap: 50,
                nameTextStyle: { fontSize: 18, fontWeight: 'bold', color: '#000' },
                splitLine: { lineStyle: { type: 'dashed', color: '#E2E8F0' } },
            },
            series: [{
                type: 'bar',
                data: data.map(d => ({
                    value: d.value,
                    itemStyle: { color: getBarColor(d.label) },
                })),
                barWidth: '60%',
                label: {
                    show: true,
                    position: 'top',
                    rotate: -40,
                    fontSize: 12,
                    color: '#000',
                },
                emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.12)' } },
            }],
            grid: { top: 50, right: 50, bottom: 80, left: 60, containLabel: true },
            ...(data.length > 20 ? {
                dataZoom: [{ type: 'slider', start: 0, end: Math.min(100, (20 / data.length) * 100) }],
            } : {}),
        };

        const computedWidth = Math.max(600, data.length * 100);

        return (
            <Box
                sx={{
                    backgroundColor: '#f9f9f9',
                    borderRadius: 2,
                    p: 2,
                    mb: 2
                }}
            >
                <Typography variant="subtitle1" align="center" sx={{ mb: 1 }}>
                    {title}
                </Typography>
                <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ width: computedWidth, height: 400 }}>
                        <ReactEChartsCore
                            echarts={echarts}
                            option={option}
                            theme="geomotica"
                            style={{ height: '100%', width: '100%' }}
                            opts={{ renderer: 'canvas' }}
                            notMerge
                            lazyUpdate
                            onChartReady={(instance) => {
                                chartInstancesRef.current[chartIndex] = instance;
                            }}
                        />
                    </Box>
                </Box>
            </Box>
        );
    };

    const handleAddChart = () => {
        onChangeChartsConfig((prev) => [
            ...prev,
            {
                xAxis: '',
                yAxis: '',
                filterProp: '',
                filterValue: '',
                calcType: 'average',
                editMode: true
            }
        ]);
    };

    return (
        <Box
            sx={{
                p: 2,
                borderRadius: 2,
                width: '100%',
                backgroundColor: '#fafafa',
                mb: 3
            }}
        >
            <Typography variant="h5" gutterBottom>
                Plantillas de gráficas
            </Typography>

            <Grid container spacing={2}>
                {chartsConfig.map((chart, idx) => {
                    const currentAnalysis = analysisData[idx] || null;
                    const chartTitle = getChartTitle(chart, idx);

                    let possibleFilterValues = chart.filterProp
                        ? distinctValuesMap[chart.filterProp] || []
                        : [];
                    possibleFilterValues = sortValues(possibleFilterValues);

                    const isLoading = !!chartLoadingStates[idx];

                    return (
                        <Grid item xs={12} sm={6} md={4} key={idx}>
                            <Card sx={{ position: 'relative' }}>
                                <CardHeader
                                    title={`Gráfica #${idx + 1}`}
                                    action={
                                        <IconButton color="error" onClick={() => onRemoveChart(idx)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    }
                                />
                                <CardContent>
                                    {chart.editMode ? (
                                        <Box sx={{ mb: 2 }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Eje X</InputLabel>
                                                        <Select
                                                            label="Eje X"
                                                            value={chart.xAxis}
                                                            onChange={(e) =>
                                                                handleConfigChange(idx, 'xAxis', e.target.value)
                                                            }
                                                        >
                                                            <MenuItem value="">(Seleccione)</MenuItem>
                                                            {availableProps.map((prop) => (
                                                                <MenuItem key={prop} value={prop}>
                                                                    {prop}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Eje Y</InputLabel>
                                                        <Select
                                                            label="Eje Y"
                                                            value={chart.yAxis}
                                                            onChange={(e) =>
                                                                handleConfigChange(idx, 'yAxis', e.target.value)
                                                            }
                                                        >
                                                            <MenuItem value="">(Seleccione)</MenuItem>
                                                            {availableProps.map((prop) => (
                                                                <MenuItem key={prop} value={prop}>
                                                                    {prop}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Filtrar por (propiedad)</InputLabel>
                                                        <Select
                                                            label="Filtrar por (propiedad)"
                                                            value={chart.filterProp}
                                                            onChange={(e) => {
                                                                handleConfigChange(idx, 'filterProp', e.target.value);
                                                                handleConfigChange(idx, 'filterValue', '');
                                                            }}
                                                        >
                                                            <MenuItem value="">(Sin filtro)</MenuItem>
                                                            {availableProps.map((prop) => (
                                                                <MenuItem key={prop} value={prop}>
                                                                    {prop}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>Operación</InputLabel>
                                                        <Select
                                                            label="Operación"
                                                            value={chart.calcType || 'average'}
                                                            onChange={(e) =>
                                                                handleConfigChange(idx, 'calcType', e.target.value)
                                                            }
                                                        >
                                                            <MenuItem value="average">Promedio</MenuItem>
                                                            <MenuItem value="sum">Suma</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>

                                            <Box sx={{ textAlign: 'right', mt: 2 }}>
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => {
                                                        toggleEditMode(idx, false);
                                                        onAnalyzeOneChart(idx);
                                                    }}
                                                >
                                                    Guardar
                                                </Button>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <>
                                            {chart.filterProp && chart.filterProp !== '' && (
                                                <Box sx={{ mb: 2 }}>
                                                    <FormControl fullWidth size="small">
                                                        <InputLabel>{`Filtrar por ${chart.filterProp}`}</InputLabel>
                                                        <Select
                                                            label={`Filtrar por ${chart.filterProp}`}
                                                            value={chart.filterValue || ''}
                                                            onChange={(e) =>
                                                                handleDynamicFilterChange(idx, e.target.value)
                                                            }
                                                        >
                                                            <MenuItem value="">
                                                                (Cualquiera)
                                                            </MenuItem>
                                                            {possibleFilterValues.map((val) => (
                                                                <MenuItem key={val} value={val}>
                                                                    {val}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Box>
                                            )}

                                            {isLoading ? (
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        justifyContent: 'center',
                                                        alignItems: 'center',
                                                        height: 300
                                                    }}
                                                >
                                                    <CircularProgress />
                                                </Box>
                                            ) : currentAnalysis && currentAnalysis.chartData ? (
                                                renderBarChart(
                                                    transformAndSortChartData(currentAnalysis.chartData),
                                                    chartTitle,
                                                    idx,
                                                    chart
                                                )
                                            ) : (
                                                <Typography variant="body2">
                                                    No hay datos para esta gráfica
                                                </Typography>
                                            )}

                                            <Box sx={{ textAlign: 'right' }}>
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => toggleEditMode(idx, true)}
                                                >
                                                    Editar
                                                </Button>
                                            </Box>
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            <Box sx={{ textAlign: 'right', mt: 2 }}>
                <Button variant="contained" onClick={handleAddChart}>
                    AGREGAR OTRA GRÁFICA
                </Button>
            </Box>
        </Box>
    );
});

export default ChartTemplates;
